import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Settings,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const PhysicsMotionSimulator = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);

  // 仿真状态
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1);

  // 物体数量
  const [objectCount, setObjectCount] = useState(2);

  // 显示的图像类型
  const [showCharts, setShowCharts] = useState({
    position: true,
    velocity: true,
    acceleration: false,
  });

  // 物体参数 - 根据题目设置
  const [objects, setObjects] = useState([
    {
      id: 1,
      name: "汽车A",
      color: "#3b82f6",
      x0: 0,
      v0: 4,
      a: 0,
      stopTime: null,
      motionType: "uniform",
    },
    {
      id: 2,
      name: "汽车B",
      color: "#ef4444",
      x0: 7,
      v0: 10,
      a: -2,
      stopTime: null,
      motionType: "brake",
    },
  ]);

  // 计算物体在时间t的位置、速度、加速度
  const calculateMotion = useCallback((obj, t) => {
    let position, velocity, acceleration;

    // 如果已经停止
    if (obj.stopTime !== null && t >= obj.stopTime) {
      const stopPos =
        obj.x0 +
        obj.v0 * obj.stopTime +
        0.5 * obj.a * obj.stopTime * obj.stopTime;
      return {
        position: stopPos,
        velocity: 0,
        acceleration: 0,
      };
    }

    switch (obj.motionType) {
      case "uniform":
        position = obj.x0 + obj.v0 * t;
        velocity = obj.v0;
        acceleration = 0;
        break;

      case "accelerated":
        position = obj.x0 + obj.v0 * t + 0.5 * obj.a * t * t;
        velocity = obj.v0 + obj.a * t;
        acceleration = obj.a;
        break;

      case "brake":
        // 刹车：计算停止时间
        if (obj.stopTime === null && obj.v0 > 0 && obj.a < 0) {
          const stopTime = -obj.v0 / obj.a;
          obj.stopTime = stopTime;
        }

        if (obj.stopTime !== null && t >= obj.stopTime) {
          const stopPos =
            obj.x0 +
            obj.v0 * obj.stopTime +
            0.5 * obj.a * obj.stopTime * obj.stopTime;
          position = stopPos;
          velocity = 0;
          acceleration = 0;
        } else {
          position = obj.x0 + obj.v0 * t + 0.5 * obj.a * t * t;
          velocity = Math.max(0, obj.v0 + obj.a * t);
          acceleration = velocity > 0 ? obj.a : 0;
        }
        break;

      default:
        position = obj.x0;
        velocity = 0;
        acceleration = 0;
    }

    return { position, velocity, acceleration };
  }, []);

  // 追及状态管理
  const [hasReachedMaxDistance, setHasReachedMaxDistance] = useState(false);

  // 计算追及状态
  const getChaseStatus = useCallback(
    (time) => {
      if (objectCount < 2)
        return { distance: 0, isMaxDistance: false, hasCaught: false };

      const state1 = calculateMotion(objects[0], time);
      const state2 = calculateMotion(objects[1], time);

      const distance = Math.abs(state2.position - state1.position);

      // 检查是否追上（位置相等且A在B后面开始）
      const hasCaught =
        state1.position >= state2.position && objects[0].x0 < objects[1].x0;

      // 检查是否达到最大距离（速度相等时）
      const isMaxDistance =
        Math.abs(state1.velocity - state2.velocity) < 0.01 &&
        state1.velocity > 0 &&
        state2.velocity > 0;

      return { distance, isMaxDistance, hasCaught };
    },
    [objects, objectCount, calculateMotion]
  );

  // 设置高DPI Canvas
  const setupHighDPICanvas = useCallback((canvas, width, height) => {
    const ctx = canvas.getContext("2d");
    const devicePixelRatio = window.devicePixelRatio || 1;

    canvas.width = width * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";

    ctx.scale(devicePixelRatio, devicePixelRatio);
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "start";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    return { ctx, width, height, devicePixelRatio };
  }, []);

  // 计算数据范围
  const calculateDataRange = useCallback(
    (type, maxTime) => {
      const activeObjects = objects.slice(0, objectCount);
      let allValues = [];

      for (let t = 0; t <= maxTime; t += 0.1) {
        activeObjects.forEach((obj) => {
          const state = calculateMotion(obj, t);
          let value;
          switch (type) {
            case "position":
              value = state.position;
              break;
            case "velocity":
              value = state.velocity;
              break;
            case "acceleration":
              value = state.acceleration;
              break;
            default:
              value = 0;
          }
          allValues.push(value);
        });
      }

      if (allValues.length === 0) {
        return { min: -1, max: 1 };
      }

      const minValue = Math.min(...allValues);
      const maxValue = Math.max(...allValues);
      const range = maxValue - minValue;
      const margin = Math.max(range * 0.1, 0.5);

      return {
        min: minValue - margin,
        max: maxValue + margin,
      };
    },
    [objects, objectCount, calculateMotion]
  );

  // 绘制图表
  const drawChart = useCallback(
    (ctx, type, title, y, height, width, unit, primaryColor) => {
      // 图表背景
      const bgGradient = ctx.createLinearGradient(50, y, 50, y + height);
      bgGradient.addColorStop(0, "#ffffff");
      bgGradient.addColorStop(1, "#f9fafb");
      ctx.fillStyle = bgGradient;
      ctx.fillRect(50, y, width, height);

      // 图表边框
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 2;
      ctx.strokeRect(50, y, width, height);

      // 标题
      ctx.fillStyle = "#1f2937";
      ctx.font = "bold 18px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(title, 60, y - 15);

      // 计算数据范围
      const maxTime = Math.max(20, currentTime + 5);
      const dataRange = calculateDataRange(type, maxTime);
      const timeScale = width / maxTime;
      const valueRange = dataRange.max - dataRange.min;
      const valueScale = height / valueRange;

      // 绘制网格线
      ctx.strokeStyle = "#f3f4f6";
      ctx.lineWidth = 1;

      // 垂直网格线
      for (let i = 0; i <= 10; i++) {
        const gridX = Math.floor(50 + (width * i) / 10) + 0.5;
        ctx.beginPath();
        ctx.moveTo(gridX, y);
        ctx.lineTo(gridX, y + height);
        ctx.stroke();
      }

      // 水平网格线
      for (let i = 0; i <= 8; i++) {
        const gridY = Math.floor(y + (height * i) / 8) + 0.5;
        ctx.beginPath();
        ctx.moveTo(50, gridY);
        ctx.lineTo(50 + width, gridY);
        ctx.stroke();
      }

      // 绘制零线
      if (dataRange.min <= 0 && dataRange.max >= 0) {
        const zeroY =
          Math.floor(y + height - (0 - dataRange.min) * valueScale) + 0.5;
        ctx.strokeStyle = "#9ca3af";
        ctx.lineWidth = 2;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(50, zeroY);
        ctx.lineTo(50 + width, zeroY);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // 绘制数据线
      const activeObjects = objects.slice(0, objectCount);

      activeObjects.forEach((obj, objIndex) => {
        const lineColor = obj.color;

        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 3;
        ctx.beginPath();

        let hasStarted = false;
        const step = Math.min(0.05, maxTime / 1000);

        for (let t = 0; t <= currentTime; t += step) {
          const state = calculateMotion(obj, t);
          let value;

          switch (type) {
            case "position":
              value = state.position;
              break;
            case "velocity":
              value = state.velocity;
              break;
            case "acceleration":
              value = state.acceleration;
              break;
            default:
              value = 0;
          }

          const x = 50 + t * timeScale;
          const plotY = y + height - (value - dataRange.min) * valueScale;

          if (!hasStarted) {
            ctx.moveTo(x, plotY);
            hasStarted = true;
          } else {
            ctx.lineTo(x, plotY);
          }
        }

        ctx.stroke();

        // 当前数据点
        if (currentTime > 0) {
          const currentState = calculateMotion(obj, currentTime);
          let currentValue;

          switch (type) {
            case "position":
              currentValue = currentState.position;
              break;
            case "velocity":
              currentValue = currentState.velocity;
              break;
            case "acceleration":
              currentValue = currentState.acceleration;
              break;
            default:
              currentValue = 0;
          }

          const currentX = 50 + currentTime * timeScale;
          const currentPlotY =
            y + height - (currentValue - dataRange.min) * valueScale;

          // 点的阴影
          ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
          ctx.beginPath();
          ctx.arc(currentX + 1, currentPlotY + 1, 6, 0, 2 * Math.PI);
          ctx.fill();

          // 当前数据点
          ctx.fillStyle = lineColor;
          ctx.beginPath();
          ctx.arc(currentX, currentPlotY, 5, 0, 2 * Math.PI);
          ctx.fill();

          // 点的边框
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 2;
          ctx.stroke();

          // 显示当前值
          ctx.fillStyle = lineColor;
          ctx.font = "bold 14px sans-serif";
          ctx.textAlign = "left";
          ctx.fillText(
            `${obj.name}: ${currentValue.toFixed(2)}${unit}`,
            65,
            y + 27 + objIndex * 25
          );
        }
      });

      // 坐标轴标签 - 智能显示
      ctx.fillStyle = "#6b7280";
      ctx.font = "12px sans-serif";
      ctx.textAlign = "center";

      // 时间轴标签 - 根据宽度调整密度
      const timeSteps = width > 600 ? 10 : 5;
      for (let i = 0; i <= timeSteps; i++) {
        const time = (maxTime * i) / timeSteps;
        const x = 50 + (width * i) / timeSteps;
        ctx.fillText(`${time.toFixed(1)}`, x, y + height + 15);
      }

      // 数值轴标签
      ctx.textAlign = "right";
      const valueSteps = width > 600 ? 8 : 4;
      for (let i = 0; i <= valueSteps; i++) {
        const value =
          dataRange.min + (valueRange * (valueSteps - i)) / valueSteps;
        const plotY = y + (height * i) / valueSteps;
        ctx.fillText(`${value.toFixed(1)}`, 45, plotY + 4);
      }

      // 轴标签
      ctx.fillStyle = "#374151";
      ctx.font = "bold 14px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("时间 (s)", 50 + width / 2, y + height + 35);

      ctx.save();
      ctx.translate(20, y + height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = "center";
      ctx.fillText(
        `${
          type === "position" ? "位置" : type === "velocity" ? "速度" : "加速度"
        } (${unit})`,
        0,
        0
      );
      ctx.restore();

      // 在位置-时间图中显示追及问题的相关信息
      if (type === "position" && objectCount === 2) {
        const chaseStatus = getChaseStatus(currentTime);
        const distance = chaseStatus.distance;

        // 重新计算当前状态以获取速度
        const activeObjects = objects.slice(0, objectCount);
        const currentStates = activeObjects.map((obj) =>
          calculateMotion(obj, currentTime)
        );
        const relativeVelocity =
          currentStates[0].velocity - currentStates[1].velocity;

        // 信息面板位置（图表右上角）
        const infoX = 50 + width - 220;
        const infoY = y + 20;

        // 信息面板背景
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        ctx.fillRect(
          infoX,
          infoY,
          210,
          chaseStatus.hasCaught || chaseStatus.isMaxDistance ? 80 : 60
        );

        // 信息面板边框
        ctx.strokeStyle = "#e5e7eb";
        ctx.lineWidth = 1;
        ctx.strokeRect(
          infoX,
          infoY,
          210,
          chaseStatus.hasCaught || chaseStatus.isMaxDistance ? 80 : 60
        );

        // 显示两车距离
        ctx.fillStyle = "#1f2937";
        ctx.font = "bold 12px sans-serif";
        ctx.textAlign = "left";
        ctx.fillText("📏 两车距离:", infoX + 8, infoY + 18);

        ctx.fillStyle = "#10b981";
        ctx.font = "bold 14px sans-serif";
        ctx.fillText(`${distance.toFixed(2)} m`, infoX + 90, infoY + 18);

        // 显示相对速度
        ctx.fillStyle = "#1f2937";
        ctx.font = "11px sans-serif";
        ctx.fillText("🚗 相对速度:", infoX + 8, infoY + 35);

        ctx.fillStyle = relativeVelocity > 0 ? "#10b981" : "#ef4444";
        ctx.font = "bold 12px sans-serif";
        ctx.fillText(
          `${relativeVelocity.toFixed(2)} m/s`,
          infoX + 90,
          infoY + 35
        );

        // 显示状态提示
        if (chaseStatus.hasCaught) {
          ctx.fillStyle = "#ef4444";
          ctx.font = "bold 11px sans-serif";
          ctx.fillText("🏁 汽车A追上汽车B！", infoX + 8, infoY + 55);
        } else if (chaseStatus.isMaxDistance) {
          ctx.fillStyle = "#f59e0b";
          ctx.font = "bold 11px sans-serif";
          ctx.fillText("📈 达到最大距离！", infoX + 8, infoY + 55);
        }
      }
    },
    [currentTime, objects, objectCount, calculateMotion, calculateDataRange]
  );

  // 绘制函数
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const chartCount = Object.values(showCharts).filter(Boolean).length;
    const canvasHeight = Math.max(600, 220 + chartCount * 190); // 确保最小高度
    const canvasWidth = Math.min(1200, window.innerWidth - 32);

    const { ctx, width, height } = setupHighDPICanvas(
      canvas,
      canvasWidth,
      canvasHeight
    );

    // 清空画布
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, "#f8fafc");
    gradient.addColorStop(1, "#e2e8f0");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // 计算当前状态
    const activeObjects = objects.slice(0, objectCount);
    const currentStates = activeObjects.map((obj) =>
      calculateMotion(obj, currentTime)
    );

    // 绘制运动轨道
    const trackY = 100;
    const trackLength = width - 100;

    // 计算轨道的范围 - 从0开始显示
    const maxPosition = Math.max(
      30,
      Math.max(...currentStates.map((state) => state.position)) + 15
    );
    const minPosition = Math.min(
      0,
      Math.min(...currentStates.map((state) => state.position)) - 5
    );
    const totalRange = maxPosition - minPosition;
    const scale = trackLength / totalRange;
    const trackStart = 50;

    // 绘制轨道背景
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(50, trackY - 15, trackLength, 30);
    ctx.strokeStyle = "#d1d5db";
    ctx.lineWidth = 2;
    ctx.strokeRect(50, trackY - 15, trackLength, 30);

    // 绘制轨道中心线
    ctx.strokeStyle = "#9ca3af";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    const centerLineY = Math.floor(trackY) + 0.5;
    ctx.moveTo(trackStart, centerLineY);
    ctx.lineTo(trackStart + trackLength, centerLineY);
    ctx.stroke();
    ctx.setLineDash([]);

    // 绘制刻度 - 智能标签显示
    ctx.fillStyle = "#6b7280";
    ctx.font = "12px sans-serif";
    ctx.textAlign = "center";

    // 根据屏幕宽度决定标签密度
    const labelStep =
      width < 600
        ? Math.max(5, Math.ceil(totalRange / 8))
        : Math.max(2, Math.ceil(totalRange / 12));
    const startLabel = Math.ceil(minPosition / labelStep) * labelStep;

    for (let i = startLabel; i <= maxPosition; i += labelStep) {
      const x = trackStart + (i - minPosition) * scale;
      if (x >= trackStart && x <= trackStart + trackLength) {
        ctx.strokeStyle = "#d1d5db";
        ctx.lineWidth = 1;
        ctx.beginPath();
        const alignedX = Math.floor(x) + 0.5;
        ctx.moveTo(alignedX, trackY - 8);
        ctx.lineTo(alignedX, trackY + 8);
        ctx.stroke();

        // 只在有足够空间时显示标签
        const labelWidth = ctx.measureText(`${i}m`).width;
        const hasSpace = width < 600 ? i % (labelStep * 2) === 0 : true;
        if (hasSpace) {
          ctx.fillText(`${i}m`, x, trackY + 25);
        }
      }
    }

    // 绘制物体
    activeObjects.forEach((obj, index) => {
      const state = currentStates[index];
      const objX = trackStart + (state.position - minPosition) * scale;

      if (objX >= trackStart - 20 && objX <= trackStart + trackLength + 20) {
        // 物体阴影
        ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
        ctx.beginPath();
        ctx.arc(objX + 2, trackY + 2, 10, 0, 2 * Math.PI);
        ctx.fill();

        // 物体主体
        ctx.fillStyle = obj.color;
        ctx.beginPath();
        ctx.arc(objX, trackY, 10, 0, 2 * Math.PI);
        ctx.fill();

        // 物体边框
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
        ctx.stroke();

        // 物体标签
        ctx.fillStyle = obj.color;
        ctx.font = "bold 14px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(obj.name, objX, trackY - 25);

        // 速度箭头
        if (Math.abs(state.velocity) > 0.1) {
          const arrowLength = Math.min(60, Math.abs(state.velocity) * 8);
          const arrowDirection = state.velocity > 0 ? 1 : -1;

          // 箭头主体
          ctx.strokeStyle = obj.color;
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(objX, trackY - 40);
          ctx.lineTo(objX + arrowLength * arrowDirection, trackY - 40);

          // 箭头头部
          const headSize = 8;
          ctx.lineTo(
            objX + (arrowLength - headSize) * arrowDirection,
            trackY - 40 - headSize / 2
          );
          ctx.moveTo(objX + arrowLength * arrowDirection, trackY - 40);
          ctx.lineTo(
            objX + (arrowLength - headSize) * arrowDirection,
            trackY - 40 + headSize / 2
          );
          ctx.stroke();

          // 速度值 - 响应式显示
          if (width > 500) {
            ctx.fillStyle = obj.color;
            ctx.font = "12px sans-serif";
            ctx.fillText(
              `v=${state.velocity.toFixed(1)}m/s`,
              objX,
              trackY - 50
            );
          }
        }

        // 位置值 - 响应式显示
        if (width > 400) {
          ctx.fillStyle = "#6b7280";
          ctx.font = "11px sans-serif";
          ctx.fillText(`x=${state.position.toFixed(1)}m`, objX, trackY + 35);
        }
      }
    });

    // 绘制距离信息（追及问题）
    if (objectCount === 2 && currentStates.length >= 2) {
      const chaseStatus = getChaseStatus(currentTime);
      const distance = chaseStatus.distance;

      // 绘制距离连线
      const obj1X =
        trackStart + (currentStates[0].position - minPosition) * scale;
      const obj2X =
        trackStart + (currentStates[1].position - minPosition) * scale;

      if (
        obj1X >= trackStart - 20 &&
        obj1X <= trackStart + trackLength + 20 &&
        obj2X >= trackStart - 20 &&
        obj2X <= trackStart + trackLength + 20
      ) {
        // 距离连线
        ctx.strokeStyle = "#10b981";
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(obj1X, trackY + 20);
        ctx.lineTo(obj2X, trackY + 20);
        ctx.stroke();
        ctx.setLineDash([]);

        // 距离值 - 在轨道下方显示
        if (width > 500) {
          const midX = (obj1X + obj2X) / 2;
          ctx.fillStyle = "#10b981";
          ctx.font = "bold 14px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(`距离: ${distance.toFixed(2)}m`, midX, trackY + 60);

          // 状态提示
          if (chaseStatus.hasCaught) {
            ctx.fillStyle = "#ef4444";
            ctx.fillText("汽车A追上汽车B！", midX, trackY + 80);
          } else if (chaseStatus.isMaxDistance) {
            ctx.fillStyle = "#f59e0b";
            ctx.fillText("达到最大距离！", midX, trackY + 80);
          }
        }
      }
    }

    // 绘制图表
    let chartY = 200; // 增加起始位置，给轨道更多空间
    const chartHeight = 140;
    const chartWidth = width - 100;

    if (showCharts.position) {
      drawChart(
        ctx,
        "position",
        "位置-时间图 (x-t)",
        chartY,
        chartHeight,
        chartWidth,
        "m",
        "#3b82f6"
      );
      chartY += chartHeight + 50;
    }

    if (showCharts.velocity) {
      drawChart(
        ctx,
        "velocity",
        "速度-时间图 (v-t)",
        chartY,
        chartHeight,
        chartWidth,
        "m/s",
        "#10b981"
      );
      chartY += chartHeight + 50;
    }

    if (showCharts.acceleration) {
      drawChart(
        ctx,
        "acceleration",
        "加速度-时间图 (a-t)",
        chartY,
        chartHeight,
        chartWidth,
        "m/s²",
        "#f59e0b"
      );
    }
  }, [
    currentTime,
    objects,
    objectCount,
    showCharts,
    calculateMotion,
    getChaseStatus,
    setupHighDPICanvas,
    drawChart,
    hasReachedMaxDistance,
  ]);

  // 动画循环
  const animate = useCallback(() => {
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
    }

    const realElapsed = (Date.now() - startTimeRef.current) / 1000;
    const simulatedTime = realElapsed * simulationSpeed;
    setCurrentTime(simulatedTime);

    // 检查追及状态
    let shouldPause = false;
    if (objectCount === 2) {
      const chaseStatus = getChaseStatus(simulatedTime);

      // 如果达到最大距离且之前没有达到过，则暂停
      if (chaseStatus.isMaxDistance && !hasReachedMaxDistance) {
        setHasReachedMaxDistance(true);
        shouldPause = true;
      }

      // 如果追上了，则停止
      if (chaseStatus.hasCaught) {
        shouldPause = true;
      }
    }

    draw();

    if (isPlaying && simulatedTime < 60 && !shouldPause) {
      animationRef.current = requestAnimationFrame(animate);
    } else if (shouldPause) {
      setIsPlaying(false);
    }
  }, [
    isPlaying,
    simulationSpeed,
    draw,
    getChaseStatus,
    objectCount,
    hasReachedMaxDistance,
  ]);

  // 开始/暂停动画
  useEffect(() => {
    if (isPlaying) {
      if (!startTimeRef.current) {
        startTimeRef.current =
          Date.now() - (currentTime / simulationSpeed) * 1000;
      } else {
        startTimeRef.current =
          Date.now() - (currentTime / simulationSpeed) * 1000;
      }
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, animate, currentTime, simulationSpeed, hasReachedMaxDistance]);

  // 重置仿真
  const reset = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    startTimeRef.current = null;
    setHasReachedMaxDistance(false);
    setObjects((prev) => prev.map((obj) => ({ ...obj, stopTime: null })));
  };

  // 重置为题目参数
  const resetToDefaultProblem = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    startTimeRef.current = null;
    setHasReachedMaxDistance(false);
    setObjectCount(2);
    setObjects([
      {
        id: 1,
        name: "汽车A",
        color: "#3b82f6",
        x0: 0,
        v0: 4,
        a: 0,
        stopTime: null,
        motionType: "uniform",
      },
      {
        id: 2,
        name: "汽车B",
        color: "#ef4444",
        x0: 7,
        v0: 10,
        a: -2,
        stopTime: null,
        motionType: "brake",
      },
    ]);
    setShowCharts({
      position: true,
      velocity: true,
      acceleration: false,
    });
  };

  // 切换播放状态
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  // 更新物体参数
  const updateObject = (index, field, value) => {
    setObjects((prev) => {
      const newObjects = [...prev];
      const numValue = value === "" ? 0 : parseFloat(value);
      newObjects[index] = {
        ...newObjects[index],
        [field]: isNaN(numValue) ? 0 : numValue,
        stopTime: null,
      };
      return newObjects;
    });
  };

  // 更新运动类型
  const updateMotionType = (index, motionType) => {
    setObjects((prev) => {
      const newObjects = [...prev];
      const updatedObj = { ...newObjects[index], motionType, stopTime: null };

      if (motionType === "brake" && updatedObj.a >= 0) {
        updatedObj.a = -2;
      }

      newObjects[index] = updatedObj;
      return newObjects;
    });
  };

  // 绘制初始状态
  useEffect(() => {
    const timer = setTimeout(() => {
      draw();
    }, 100);

    return () => clearTimeout(timer);
  }, [draw]);

  return (
    <div className="w-full mx-auto p-4 bg-gradient-to-br from-blue-50 to-purple-50 min-h-screen">
      <div className="bg-white rounded-xl shadow-xl overflow-hidden">
        {/* 标题栏 */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white p-6">
          <h1 className="text-3xl font-bold">高中物理直线运动探索工具</h1>
          <p className="text-blue-100 mt-2">当前模拟：汽车追及问题</p>
          <div className="text-sm text-blue-100 mt-2 opacity-90">
            <p>
              汽车A以4m/s匀速行驶，汽车B在前方7m处以10m/s行驶并开始以-2m/s²刹车
            </p>
          </div>
        </div>

        {/* 控制面板 */}
        <div className="p-4 border-b bg-gradient-to-r from-gray-50 to-blue-50">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={togglePlay}
                className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
              >
                {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                {isPlaying ? "暂停" : "开始"}
              </button>

              <button
                onClick={reset}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm"
              >
                <RotateCcw size={16} />
                重置
              </button>

              <button
                onClick={resetToDefaultProblem}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm"
              >
                📚 题目
              </button>

              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-2 px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm"
              >
                <Settings size={16} />
                {showSettings ? (
                  <ChevronUp size={14} />
                ) : (
                  <ChevronDown size={14} />
                )}
                <span className="hidden sm:inline">设置</span>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
              {/* 模拟速度控制 */}
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg shadow-md border">
                <label className="text-xs sm:text-sm font-medium text-gray-700">
                  速度:
                </label>
                <select
                  value={simulationSpeed}
                  onChange={(e) =>
                    setSimulationSpeed(parseFloat(e.target.value))
                  }
                  className="text-xs sm:text-sm border-none bg-transparent focus:outline-none"
                >
                  <option value={0.25}>0.25x</option>
                  <option value={0.5}>0.5x</option>
                  <option value={1}>1x</option>
                  <option value={2}>2x</option>
                  <option value={4}>4x</option>
                </select>
              </div>

              <div className="text-lg sm:text-xl font-mono bg-white px-3 sm:px-4 py-2 rounded-lg shadow-md border">
                时间:{" "}
                <span className="text-blue-600 font-bold">
                  {currentTime.toFixed(1)}s
                </span>
                {hasReachedMaxDistance && (
                  <div className="text-xs text-orange-600 mt-1">
                    已达最大距离，可继续观察
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 主显示区域 */}
        <div className="w-full">
          <canvas
            ref={canvasRef}
            className="w-full border-b bg-white block"
            style={{
              maxWidth: "100%",
              imageRendering: "crisp-edges",
            }}
          />
        </div>

        {/* 设置面板 */}
        {showSettings && (
          <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 border-t">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* 基本设置 */}
              <div className="bg-white p-4 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  基本设置
                </h3>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    物体数量
                  </label>
                  <select
                    value={objectCount}
                    onChange={(e) => setObjectCount(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value={1}>1个物体</option>
                    <option value={2}>2个物体（追及问题）</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    显示图表
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={showCharts.position}
                        onChange={(e) =>
                          setShowCharts((prev) => ({
                            ...prev,
                            position: e.target.checked,
                          }))
                        }
                        className="mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-blue-600 font-medium">
                        位置-时间图 (x-t)
                      </span>
                    </label>
                    <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={showCharts.velocity}
                        onChange={(e) =>
                          setShowCharts((prev) => ({
                            ...prev,
                            velocity: e.target.checked,
                          }))
                        }
                        className="mr-3 w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-green-600 font-medium">
                        速度-时间图 (v-t)
                      </span>
                    </label>
                    <label className="flex items-center p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={showCharts.acceleration}
                        onChange={(e) =>
                          setShowCharts((prev) => ({
                            ...prev,
                            acceleration: e.target.checked,
                          }))
                        }
                        className="mr-3 w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                      />
                      <span className="text-yellow-600 font-medium">
                        加速度-时间图 (a-t)
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* 物体参数设置 */}
              {objects.slice(0, objectCount).map((obj, index) => (
                <div
                  key={obj.id}
                  className="bg-white p-4 rounded-lg shadow-md border-l-4"
                  style={{ borderLeftColor: obj.color }}
                >
                  <h4
                    className="font-semibold mb-4 text-lg"
                    style={{ color: obj.color }}
                  >
                    {obj.name} 参数设置
                  </h4>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        运动类型
                      </label>
                      <select
                        value={obj.motionType}
                        onChange={(e) =>
                          updateMotionType(index, e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="uniform">匀速运动</option>
                        <option value="accelerated">匀变速运动</option>
                        <option value="brake">刹车停止</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        初始位置 (m)
                      </label>
                      <input
                        type="number"
                        value={obj.x0}
                        onChange={(e) =>
                          updateObject(index, "x0", e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        step="0.1"
                        placeholder="输入初始位置"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        初始速度 (m/s)
                      </label>
                      <input
                        type="number"
                        value={obj.v0}
                        onChange={(e) =>
                          updateObject(index, "v0", e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        step="0.1"
                        placeholder="输入初始速度"
                      />
                    </div>

                    {(obj.motionType === "accelerated" ||
                      obj.motionType === "brake") && (
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          加速度 (m/s²)
                          {obj.motionType === "brake" && (
                            <span className="text-red-500 text-xs ml-1">
                              (建议负值)
                            </span>
                          )}
                        </label>
                        <input
                          type="number"
                          value={obj.a}
                          onChange={(e) =>
                            updateObject(index, "a", e.target.value)
                          }
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          step="0.1"
                          placeholder={
                            obj.motionType === "brake"
                              ? "输入负的加速度"
                              : "输入加速度"
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default PhysicsMotionSimulator;
